import Logo from '@/components/Logo';
import { GameConfig } from '@/config/game';
import classnames from '@/utilities/classnames';
import React, { createContext, useContext } from 'react';

export type TableAlignColumns = 'left' | 'center' | 'right';

export interface TableProps {
  readonly rowsHead?: string[];
  readonly rows: string[][];
  readonly alignColumns?: TableAlignColumns[];
  readonly theme?: GameConfig;
  readonly caption?: string;
  readonly footnote?: string;
}

const TableContext = createContext<{
  getColumnStyle: (index: number) => React.CSSProperties;
  getHead: (index: number) => string;
}>({
  getColumnStyle: () => ({}),
  getHead: () => '',
});

const useTableContext = () => {
  return useContext(TableContext);
};

const Table = ({
  children,
  alignColumns = [],
}: {
  children: React.ReactNode;
  alignColumns?: TableAlignColumns[];
}) => {
  children = React.Children.toArray(children);
  if (!children || !Array.isArray(children)) {
    throw new Error('table children are required');
  }
  const caption = children.find((child) => child.type === Table.Caption);
  const head = children.find((child) => child.type === Table.Head);
  const body = children.find((child) => child.type === Table.Body);
  const footNote = children.find((child) => child.type === Table.FootNote);

  const rows = body ? body.props.rows : [];
  const rowsHead = head ? head.props.rowsHead : [];

  const getColumnStyle = (index: number): React.CSSProperties => {
    return { textAlign: alignColumns[index] || undefined };
  };

  const getHead = (index: number): string => {
    if (!rowsHead) {
      return '';
    }
    return rowsHead[index];
  };

  return (
    <TableContext.Provider value={{ getColumnStyle, getHead }}>
      <div
        className={classnames('Table', {
          'Table--compact': rows && rows[0] && rows[0].length >= 3,
        })}
      >
        {caption}
        <div className="Table__wrap">
          <div className="Table__container">
            <table>
              {head}
              {body}
            </table>
          </div>
          {footNote}
        </div>
      </div>
    </TableContext.Provider>
  );
};

Table.Caption = function TableCaption({
  caption,
  theme,
}: {
  caption: string | undefined;
  theme: GameConfig | undefined;
}) {
  if (!caption) {
    return null;
  }
  return (
    <div
      className={classnames(
        'Table__caption',
        `Table__caption--${theme ? theme.shortname : 'default'}`,
        {
          'Table__caption--themed': theme !== undefined,
        }
      )}
    >
      {theme && <Logo variant={theme.logo} />}
      <span>{caption}</span>
    </div>
  );
};

Table.Head = function TableHead({ rowsHead }: { rowsHead: string[] }) {
  const { getColumnStyle, getHead } = useTableContext();
  return (
    <thead>
      <tr>
        {rowsHead.map((item, columnIndex) => (
          <th
            key={item}
            style={getColumnStyle(columnIndex)}
            data-head={getHead(columnIndex)}
            dangerouslySetInnerHTML={{ __html: item }}
          />
        ))}
      </tr>
    </thead>
  );
};

Table.Body = function TableBody({ rows }: { rows: string[][] }) {
  const { getColumnStyle, getHead } = useTableContext();
  return (
    <tbody>
      {rows.map((row, rowIndex) => (
        <tr key={rowIndex}>
          {row.map((cell, columnIndex) => (
            <td
              key={columnIndex}
              data-head={getHead(columnIndex)}
              style={getColumnStyle(columnIndex)}
              dangerouslySetInnerHTML={{ __html: cell }}
            />
          ))}
        </tr>
      ))}
    </tbody>
  );
};

Table.FootNote = function TableFootNote({ footnote }: { footnote: string }) {
  return <small className="Table__footnote">{footnote}</small>;
};

export default Table;
